import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { ToastProvider } from '@up2d8/ui';
import { OfflineProvider } from '../offline/use-offline';
import { registerServiceWorker } from '../offline/register';
import { SessionProvider, useSession } from '../shared/session';
import { PlatformBrand } from '../shared/platform-brand';

import { useOrgTypeTerms } from '../shared/use-org-type-terms';
import { buildRouter } from './router';

/**
 * De router heeft de sessie nodig in zijn context (de poortwachters gebruiken hem),
 * dus hij wordt binnen de SessionProvider gebouwd.
 */
function RouterWithSession() {
  const session = useSession();
  // De ref houdt de laatste sessie bij; de router wordt precies één keer gebouwd.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const router = useMemo(() => buildRouter(() => sessionRef.current), []);
  // De woorden van het type organisatie (ORG-2): "lid" in een vereniging.
  useOrgTypeTerms();

  return (
    // Alleen een medewerker heeft een offline-app: hij heeft een `personId`. Een beheerder achter een
    // bureau en een platformbeheerder hebben niets te synchroniseren (ADR-0046).
    <OfflineProvider api={session.api} active={session.profile?.personId != null}>
      {/* Het platformmerk, ook vóór het aanmelden (MRK-2): het aanmeldscherm staat meteen goed. */}
      <PlatformBrand>
        <RouterProvider router={router} />
      </PlatformBrand>
    </OfflineProvider>
  );
}

export function AppProviders() {
  /**
   * De service worker meldt zich aan bij het starten, en niet pas als iemand meldingen aanzet.
   *
   * Hij doet sinds Stap 12 ook het offline werk: zonder registratie bij de start opent de app niet
   * zonder netwerk, en dan is de eerste keer dat iemand hem nodig heeft precies de keer dat hij er
   * niet is.
   */
  useEffect(() => registerServiceWorker(), []);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Een 401 lost de api-client zelf op met een stille refresh; opnieuw
            // proberen op andere fouten heeft in de praktijk weinig zin.
            retry: false,
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ToastProvider>
          <RouterWithSession />
        </ToastProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
